import type { JWT } from 'aws-amplify/auth';
import { v4 as uuid } from 'uuid';

import { KDSPermission } from '../enums';
import { CognitoUser } from '../interfaces';
import { Country, Kitchen } from '../types';

/**
 * Permissions an account without `custom:permissions` does not get.
 *
 * A kitchen account signs in with no permission attribute and falls back to
 * everything else on this enum, so a view that is for admins only has to be
 * named here as well as gated on its permission.
 */
const ADMIN_ONLY_PERMISSIONS: KDSPermission[] = [
  KDSPermission.VIEW_QUALITY_METRICS,
  KDSPermission.VIEW_KITCHEN_FLOOR,
  KDSPermission.EDIT_PRODUCTION_CONTROL,
  KDSPermission.VIEW_TOP_CONTROL,
  KDSPermission.EDIT_TRACEABILITY_VOID,
  KDSPermission.HEAD_CHEF
];

export const parseCognitoUser = (claims: JWT['payload'] = {}): CognitoUser => {
  const userAttributes = claims as Partial<Record<string, string>>;
  const permissions = userAttributes['custom:permissions'] ?? '';
  const group = userAttributes['custom:group'] ?? '';
  const userCountry = (userAttributes['custom:country'] as Country) ?? Country.BH;
  const filteredKitchens = Object.values(Kitchen).filter((k) => !k.includes('000'));
  const kitchenStaffPermissions = Object.values(KDSPermission).filter((p) => !ADMIN_ONLY_PERMISSIONS.includes(p));

  return {
    id: userAttributes.sub ?? userAttributes.email ?? uuid(),
    name: userAttributes.name ?? '',
    email: userAttributes.email ?? '',
    phoneNumber: userAttributes.phone_number ?? '',
    country: userCountry,
    kitchen: userAttributes['custom:kitchen']
      ? (userAttributes['custom:kitchen'].split(';') as Kitchen[])
      : [filteredKitchens.find((k) => k.includes(userCountry)) || Kitchen.BH001],
    group: group || '',
    permissions: permissions ? (permissions.split('|') as KDSPermission[]) : kitchenStaffPermissions
  };
};

export const getUserAttributes = (user: CognitoUser): Partial<CognitoUser> => {
  return {
    id: user.id,
    name: user.name,
    email: user.email
  };
};
